import { format, Locale } from "date-fns";
import {
  IDrawerMultitaskSubtasks,
  IDrawerTaskInstanceResponsePayload,
  IDrawerTaskResponseSnapshot,
} from "@evennt-tech/api-v2-external";
import { IGridSchema } from "ts/api/grid-schema";
import { definitionGridToSchema } from "tools/task/grid/mappers";
import {
  IOrganiser,
  ITaskComment,
  ITaskCommentResponse,
  ITaskInstance,
  ITaskResponse,
  ITaskReviewer,
  ITaskReviewerReactionFile,
  TaskResponseHistoryRole,
  TaskResponseType,
} from "@evennt-tech/api-v2-core";

/**
 * FE-side adapters for the bundled drawer-state response. The BE returns
 * raw entities; these helpers do the reshapes the drawer renderers
 * expect (submitter projection, reaction-file denorm, multitask
 * flattening, comment transform).
 */

export type ReviewerWithReactionFiles = ITaskReviewer<string> & {
  reactionFiles: ITaskReviewerReactionFile<string>[];
};

export function joinReviewersWithReactionFiles(
  reviewers: ITaskReviewer<string>[],
  reactionFiles: ITaskReviewerReactionFile<string>[]
): ReviewerWithReactionFiles[] {
  const byReviewerId = new Map<string, ITaskReviewerReactionFile<string>[]>();
  for (const rf of reactionFiles) {
    if (!rf.reviewerId) continue;
    const arr = byReviewerId.get(rf.reviewerId) ?? [];
    arr.push(rf);
    byReviewerId.set(rf.reviewerId, arr);
  }
  return reviewers.map((r) => ({
    ...r,
    reactionFiles: byReviewerId.get(r.id) ?? [],
  }));
}

/**
 * A reviewer's decision timestamp — their `approvalDate` (approved) or
 * `rejectionDate` (rejected) — formatted with the show's readable date
 * format plus 24h time, e.g. "24 Jul 2026, 12:11". Returns `null` when the
 * reviewer hasn't decided yet (both dates unset), so the caller renders no
 * timestamp. `readableDateFormat` is the spelled-month mask from
 * `getReadableString(show)` (a date-fns pattern).
 */
export function formatReviewerDecisionAt(
  reviewer: Pick<ITaskReviewer<string>, "approvalDate" | "rejectionDate">,
  readableDateFormat: string,
  locale?: Locale
): string | null {
  const decidedAt = reviewer.approvalDate ?? reviewer.rejectionDate ?? null;
  if (!decidedAt) return null;
  return format(
    new Date(decidedAt),
    `${readableDateFormat}, HH:mm`,
    locale ? { locale } : undefined
  );
}

/** Flat per-subtask row composed from the BE's three raw arrays. */
export type MultitaskSubtaskRow = {
  id: string;
  title: string | null;
  description: string | null;
  responseType: TaskResponseType | null;
  orderInParent: number;
  /** "Answer required" flag — drives the Required/Optional row label. */
  required: boolean;
  instance: ITaskInstance<string> | null;
  response: ITaskResponse<string> | null;
  payload: IDrawerTaskInstanceResponsePayload | null;
  /** Grid schema for GRID subtasks (from the BE projection). */
  gridSchema: IGridSchema | null;
};

export function composeMultitaskSubtasks(
  raw: IDrawerMultitaskSubtasks<string> | null
): MultitaskSubtaskRow[] | null {
  if (!raw) return null;
  const instanceByTaskId = new Map<string, ITaskInstance<string>>();
  for (const inst of raw.instances) instanceByTaskId.set(inst.taskId, inst);
  const responseByInstanceId = new Map<
    string,
    {
      response: ITaskResponse<string>;
      payload: IDrawerTaskInstanceResponsePayload | null;
      gridSchema: IGridSchema | null;
    }
  >();
  for (const r of raw.responses)
    responseByInstanceId.set(r.response.taskInstanceId, {
      response: r.response,
      payload: r.payload,
      gridSchema: definitionGridToSchema(r.gridSchema),
    });
  return raw.subtasks.map((sub) => {
    const instance = instanceByTaskId.get(sub.id) ?? null;
    const responseEntry = instance
      ? responseByInstanceId.get(instance.id)
      : undefined;
    return {
      id: sub.id,
      title: sub.title ?? null,
      description: sub.description ?? null,
      responseType: sub.responseType ?? null,
      orderInParent: sub.orderInParent ?? 0,
      // `isRequired` isn't on the installed api-v2-core `ITask` typings
      // yet though the BE returns it. Default `true` when absent.
      required: (sub as { isRequired?: boolean }).isRequired ?? true,
      instance,
      response: responseEntry?.response ?? null,
      payload: responseEntry?.payload ?? null,
      gridSchema: responseEntry?.gridSchema ?? null,
    };
  });
}

/**
 * Whether a response payload carries actual content for its type. A
 * Not-Applicable response is payload-less, so this returns `false` for it.
 */
export function hasAnswerPayload(
  responseType: TaskResponseType | null | undefined,
  payload: IDrawerTaskInstanceResponsePayload | null
): boolean {
  if (!payload || !responseType) return false;
  switch (responseType) {
    case TaskResponseType.SHORT_ANSWER:
      return !!payload.shortAnswer?.text;
    case TaskResponseType.PARAGRAPH:
      return !!payload.paragraph?.text;
    case TaskResponseType.CHECK:
      return !!payload.check?.isChecked;
    case TaskResponseType.SHORT_ANSWER_ROWS:
      return !!payload.shortAnswerRows?.rows?.length;
    case TaskResponseType.FILES:
      return !!payload.files?.files?.length;
    case TaskResponseType.MULTIPLE_CHOICE:
      return !!payload.multipleChoice?.optionId;
    case TaskResponseType.CHECKBOXES:
      return !!payload.checkboxes?.optionIds?.length;
    case TaskResponseType.GRID:
      return !!payload.grid?.cells?.length;
    default:
      return false;
  }
}

/**
 * Whether the exhibitor gave a real (non-draft, non-NA) answer. Distinct
 * from "answered": an NA subtask counts as answered for the completion
 * gate but not as a real answer, so an all-NA multitask isn't
 * re-submittable until at least one subtask has an actual answer. An answer
 * an organiser emptied by removing its files is still one (FS-1572).
 */
export function isSubtaskRealAnswer(s: MultitaskSubtaskRow): boolean {
  return (
    !!s.response &&
    !(s.response.isDraft ?? false) &&
    (!!s.response.isFileRemovalEdit ||
      hasAnswerPayload(s.responseType, s.payload))
  );
}

export type SubmittedByView = {
  displayName: string;
  email: string | null;
  role: TaskResponseHistoryRole;
  /** Submitter's company name — rendered next to the name ("Name, Company"). */
  companyName?: string | null;
  /** Organiser/exhibitor company logo URL, resolved via API. */
  logo?: string | null;
};

type SubmitterInput = {
  organiserId: string | null;
  exhibitorId: string | null;
  email: string | null;
  role: TaskResponseHistoryRole | null;
};

/**
 * Resolve a "Submitted by" display projection from raw identity columns
 * + the org's organisers list. Falls back to email when no matching
 * organiser/exhibitor row exists; `null` when nothing resolves. The
 * exhibitor branch is best-effort — pass an `exhibitorById` map for
 * richer resolution.
 */
export function resolveSubmittedBy(
  input: SubmitterInput,
  options?: {
    organisers?: IOrganiser[];
    exhibitorById?: Map<
      string,
      { name?: string | null; email?: string | null }
    >;
  }
): SubmittedByView | null {
  const organisers = options?.organisers ?? [];
  if (input.organiserId) {
    const o = organisers.find((x) => x.id === input.organiserId);
    if (o) {
      return {
        displayName: o.contactPerson ?? o.id,
        email: o.user?.email ?? null,
        role: TaskResponseHistoryRole.ORGANISER,
      };
    }
  }
  if (input.exhibitorId) {
    const e = options?.exhibitorById?.get(input.exhibitorId);
    if (e) {
      return {
        displayName: e.name ?? input.exhibitorId,
        email: e.email ?? null,
        role: TaskResponseHistoryRole.EXHIBITOR,
      };
    }
  }
  if (input.email) {
    return {
      displayName: input.email,
      email: input.email,
      role: input.role ?? TaskResponseHistoryRole.EMAIL,
    };
  }
  return null;
}

/** Compute the root "Submitted by" projection from the instance + response. */
export function resolveRootSubmittedBy(
  instance: ITaskInstance<string> | null,
  response: ITaskResponse<string> | null,
  options?: Parameters<typeof resolveSubmittedBy>[1]
): SubmittedByView | null {
  if (!instance && !response) return null;
  return resolveSubmittedBy(
    {
      organiserId:
        instance?.respondentOrganiserId ??
        response?.createdByOrganiserId ??
        null,
      exhibitorId:
        instance?.respondentExhibitorId ??
        response?.createdByExhibitorId ??
        null,
      email: response?.createdByEmail ?? null,
      role: response?.createdByRole ?? null,
    },
    options
  );
}

/** Per-history-row submitter resolver. */
export function resolveHistorySubmittedBy(
  snapshot: IDrawerTaskResponseSnapshot<string>,
  options?: Parameters<typeof resolveSubmittedBy>[1]
): SubmittedByView | null {
  return resolveSubmittedBy(
    {
      organiserId: snapshot.createdByOrganiserId,
      exhibitorId: snapshot.createdByExhibitorId,
      email: snapshot.createdByEmail,
      role: snapshot.createdByRole,
    },
    options
  );
}

/**
 * Compute `isRead` for a raw `ITaskComment` against the calling organiser
 * id: a comment authored by the caller is always read; otherwise a
 * `readBy` row with the caller's organiser id marks it read.
 */
export function computeCommentIsRead(
  comment: ITaskComment<string>,
  organiserId: string | null | undefined
): boolean {
  if (!organiserId) return false;
  if (comment.organiserId === organiserId) return true;
  return (comment.readBy ?? []).some((r) => r.organiserId === organiserId);
}

/** Map raw `ITaskComment` to the FE's `ITaskCommentResponse` shape. */
export function toCommentResponse(
  comment: ITaskComment<string>,
  organiserId: string | null | undefined
): ITaskCommentResponse<string> {
  return {
    ...comment,
    isRead: computeCommentIsRead(comment, organiserId),
  } as ITaskCommentResponse<string>;
}
